/**
 * useProgressStream Hook
 * 
 * Manages Server-Sent Events (SSE) connection for real-time progress updates.
 * Automatically reconnects on errors and cleans up on unmount.
 */

import { useState, useEffect, useRef } from 'react';
import progressService from '../services/progressService';

export const useProgressStream = (taskId, options = {}) => {
  const {
    enabled = true, // Can disable stream
    onComplete = null, // Callback when task completes
    reconnectDelay = 3000, // Delay before reconnecting (ms)
    maxReconnectAttempts = 5 // Maximum reconnection attempts
  } = options;

  const [progress, setProgress] = useState(null);
  const [error, setError] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const eventSourceRef = useRef(null);
  const reconnectAttemptsRef = useRef(0);
  const reconnectTimeoutRef = useRef(null);

  useEffect(() => {
    // Don't connect if disabled or no taskId
    if (!enabled || !taskId) {
      return;
    }

    // Clean up any existing connection
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }

    // Clear any pending reconnection
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    // Reset reconnect attempts
    reconnectAttemptsRef.current = 0;

    const connect = () => {
      try {
        eventSourceRef.current = progressService.connectToProgressStream(
          taskId,
          (updatedProgress) => {
            setProgress(updatedProgress);
            setError(null);
            setIsConnected(true);
            reconnectAttemptsRef.current = 0; // Reset on successful update
          },
          (err) => {
            console.error('Progress stream error:', err);
            setError(err);
            setIsConnected(false);
            
            // Attempt reconnection
            if (reconnectAttemptsRef.current < maxReconnectAttempts) {
              reconnectAttemptsRef.current += 1;
              reconnectTimeoutRef.current = setTimeout(() => {
                console.log(`Reconnecting to progress stream (attempt ${reconnectAttemptsRef.current}/${maxReconnectAttempts})...`);
                connect();
              }, reconnectDelay);
            } else {
              console.error('Max reconnection attempts reached');
            }
          },
          (completedProgress) => {
            setProgress(completedProgress);
            setIsConnected(false);
            if (onComplete) onComplete(completedProgress);
            if (eventSourceRef.current) {
              eventSourceRef.current.close();
              eventSourceRef.current = null;
            }
          }
        );

        // Set initial connection state
        setIsConnected(true);
      } catch (err) {
        console.error('Error connecting to progress stream:', err);
        setError(err);
        setIsConnected(false);
      }
    };

    // Initial connection
    connect();

    // Cleanup on unmount
    return () => {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
      setIsConnected(false);
    };
  }, [taskId, enabled, onComplete, reconnectDelay, maxReconnectAttempts]);

  return {
    progress,
    error,
    isConnected,
    reconnect: () => {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
      reconnectAttemptsRef.current = 0;
      // Trigger reconnection by updating taskId (temporary)
      const currentTaskId = taskId;
      // This will trigger useEffect to reconnect
    }
  };
};

